import { APPOINTMENT_OUTCOME_LABELS, APPOINTMENT_OUTCOMES, type AppointmentOutcome } from '@lia/contracts';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { StatusBadge, type StatusTone } from '../primitives/badge.js';
import { Button } from '../primitives/button.js';
import { FormField } from '../primitives/form-field.js';
import { Textarea } from '../primitives/input.js';

// Appointment outcome (setter spec §10.5, §15.7): Realizada / No show / Cancelada / Reprogramada reported by the
// responsible advisor or a manager; «resultado pendiente» when the day ended without a report.

export const APPOINTMENT_OUTCOME_TONE: Record<AppointmentOutcome, StatusTone> = {
  completed: 'success',
  no_show: 'danger',
  cancelled: 'neutral',
  rescheduled: 'warning',
};

export function AppointmentOutcomeBadge({ outcome, pending, className }: { outcome: AppointmentOutcome | null; pending: boolean; className?: string }) {
  if (outcome) return <StatusBadge tone={APPOINTMENT_OUTCOME_TONE[outcome]} className={className}>{APPOINTMENT_OUTCOME_LABELS[outcome]}</StatusBadge>;
  if (pending) return <StatusBadge tone="warning" className={className}>Resultado pendiente</StatusBadge>;
  return null;
}

export interface AppointmentOutcomeActionsProps {
  current: AppointmentOutcome | null;
  /** Realizada / No show only from the appointment time on (never inferred, never in advance). */
  attendanceReportable: boolean;
  onRecord: (outcome: AppointmentOutcome, note: string) => void;
  busy?: boolean;
  /** Whole control disabled (e.g. not the responsible advisor), with the reason shown. */
  disabledReason?: string | null;
  className?: string;
}

const ATTENDANCE: readonly AppointmentOutcome[] = ['completed', 'no_show'];

export function AppointmentOutcomeActions({ current, attendanceReportable, onRecord, busy, disabledReason, className }: AppointmentOutcomeActionsProps) {
  const [picked, setPicked] = React.useState<AppointmentOutcome | null>(null);
  const [note, setNote] = React.useState('');
  const noteId = React.useId();
  const disabled = !!disabledReason || !!busy;
  return (
    <div className={cn('grid gap-3', className)}>
      <div role="group" aria-label="Resultado de la cita" className="flex flex-wrap gap-2">
        {APPOINTMENT_OUTCOMES.map((o) => {
          const blocked = disabled || (ATTENDANCE.includes(o) && !attendanceReportable);
          return (
            <Button
              key={o}
              type="button"
              size="sm"
              variant={picked === o || (!picked && current === o) ? 'primary' : 'secondary'}
              aria-pressed={current === o}
              disabled={blocked}
              onClick={() => setPicked(o)}
            >
              {APPOINTMENT_OUTCOME_LABELS[o]}
            </Button>
          );
        })}
      </div>
      {disabledReason ? <p className="text-meta text-text-secondary">{disabledReason}</p> : null}
      {!disabledReason && !attendanceReportable ? <p className="text-meta text-text-secondary">Realizada y No show se informan a partir de la hora de la cita.</p> : null}
      {picked && !disabled ? (
        <div className="grid gap-2 rounded-card border border-border-subtle bg-surface-subtle p-3">
          <FormField id={noteId} label="Nota (opcional)" description="Lo que el equipo comercial deba saber; se guarda con el resultado.">
            <Textarea id={noteId} rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
          </FormField>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={() => onRecord(picked, note.trim())}>
              Guardar resultado
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setPicked(null)}>
              Cancelar
            </Button>
          </div>
          <p className="text-meta text-text-secondary">
            Se registrará «{APPOINTMENT_OUTCOME_LABELS[picked]}» y la etapa del prospecto cambiará en consecuencia.
          </p>
        </div>
      ) : null}
    </div>
  );
}
